import { z } from 'zod'

import { occasionType } from './product.js'

/**
 * 업체 지급 API 계약 (BIZ-16 → BIZ-38 → BIZ-148). 설계 명세서 5.6.
 *
 * 운영자가 배송 완료된 주문에 대해 업체에 공급가를 지급한다. 지급은 **(업체 × 청구월) 단위**다
 * (BIZ-148) — 청구·업체 정산 조회와 같은 청구월(billing_month) 축. 운영자가 월을 골라 생성하면
 * 업체마다 진행 중인 지급 1건에 그 달 미지급 주문이 담긴다(payout_item 1:N). 한 주문은 두 번
 * 지급되지 않는다 — DB의 payout_item 유니크가 최종 방어. 지급액은 공급 스냅샷
 * (order_supply.total_payable)에서 뽑는다. 운영자 전용(업체는 접근 없음).
 *
 * 지급은 나눠서 할 수 있다. 일부만 보내면 PARTIALLY_PAID, 예정액을 다 채우면 PAID.
 */

export const payoutStatus = z.enum(['PLANNED', 'PARTIALLY_PAID', 'PAID', 'CANCELLED'])
export type PayoutStatus = z.infer<typeof payoutStatus>

/** 아직 지급 안 된 배송완료 주문(지급 대상). */
export const payableOrder = z.object({
  orderId: z.number().int(),
  orderNo: z.string().nullable(),
  vendorId: z.number().int(),
  vendorName: z.string().nullable(),
  occasionType,
  deliveredAt: z.string().nullable(),
  billingMonth: z.string().nullable(), // 청구월 YYYY-MM — 이 달의 월 지급에 담긴다
  amount: z.number().int(), // order_supply.total_payable
})
export type PayableOrder = z.infer<typeof payableOrder>

export const payableListResponse = z.object({ items: z.array(payableOrder) })
export type PayableListResponse = z.infer<typeof payableListResponse>

/** 한 payout에 묶인 주문 한 건. 지급명세서의 한 줄이다. */
export const payoutItemView = z.object({
  orderId: z.number().int(),
  orderNo: z.string().nullable(),
  amount: z.number().int(), // 그 주문의 공급 스냅샷(order_supply.total_payable)
})
export type PayoutItemView = z.infer<typeof payoutItemView>

export const payoutView = z.object({
  id: z.number().int(),
  vendorId: z.number().int(),
  vendorName: z.string().nullable(),
  // 청구월. 월 정산 이전(BIZ-148)에 만든 취소 건은 null일 수 있다.
  billingMonth: z.string().nullable(),
  status: payoutStatus,
  plannedAmount: z.number().int(),
  paidAmount: z.number().int().nullable(), // 누적 지급액(부분 지급 포함)
  paidAt: z.string().nullable(),
  bankAccount: z.string().nullable(),
  memo: z.string().nullable(),
  createdAt: z.string(),
  // 묶인 주문들(N건). 건별 지급은 items.length === 1. 지급명세서의 항목이 된다.
  items: z.array(payoutItemView),
})
export type PayoutView = z.infer<typeof payoutView>

export const payoutListResponse = z.object({
  items: z.array(payoutView),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
})
export type PayoutListResponse = z.infer<typeof payoutListResponse>

const billingMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/)

export const payoutListQuery = z.object({
  status: payoutStatus.optional(),
  billingMonth: billingMonth.optional(),
  vendorId: z.coerce.number().int().positive().max(9_007_199_254_740_991).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  offset: z.coerce.number().int().min(0).optional().default(0),
})
export type PayoutListQuery = z.infer<typeof payoutListQuery>

/**
 * 월 지급 생성(BIZ-148). 그 청구월의 배송 완료·미지급 주문을 업체별 진행 중 지급 1건에 담는다.
 * 다시 실행해도 이중으로 담기지 않는다(멱등). 특정 업체만 좁히려면 vendorId.
 */
export const payoutGenerateRequest = z.object({
  billingMonth,
  vendorId: z.number().int().positive().max(9_007_199_254_740_991).optional(),
})
export type PayoutGenerateRequest = z.infer<typeof payoutGenerateRequest>

export const payoutGenerateResponse = z.object({
  billingMonth: z.string(),
  payouts: z.array(
    z.object({
      payoutId: z.number().int(),
      vendorId: z.number().int(),
      vendorName: z.string().nullable(),
      itemsAdded: z.number().int(),
      itemsRemoved: z.number().int(), // 담긴 뒤 취소·클레임돼 뺀 주문 수
      // 이미 일부 지급이 시작된 건이라 새 주문을 담지 않음 — 지급이 끝난 뒤 다시 생성하면 추가 지급 건으로 담긴다
      skippedLocked: z.boolean(),
    }),
  ),
})
export type PayoutGenerateResponse = z.infer<typeof payoutGenerateResponse>

/** 지급 실행. paidAmount는 **이번에 보내는 금액**이다 — 미지정 시 남은 금액 전부. */
export const payPayoutRequest = z.object({
  paidAmount: z.number().int().min(0).max(9_007_199_254_740_991).optional(),
  bankAccount: z.string().max(100).optional(),
  memo: z.string().max(500).optional(),
})
export type PayPayoutRequest = z.infer<typeof payPayoutRequest>

/**
 * 업체 자신의 정산 조회(읽기전용, BIZ-16 파생). 운영자 payout 계약과 달리 vendor 스코프 —
 * 업체는 자기 배송완료 주문의 공급가와 지급 상태만 본다. 지급 실행·묶음 생성은 운영자 전용.
 * 월(청구월, billing_month) 단위로 집계한다.
 */
export const vendorSettlementStatus = z.enum(['PENDING', 'PLANNED', 'PAID'])
export type VendorSettlementStatus = z.infer<typeof vendorSettlementStatus>

/** 배송완료 주문 한 건의 정산 관점 — 공급가와 그 주문의 지급 상태. */
export const vendorSettlementItem = z.object({
  orderId: z.number().int(),
  orderNo: z.string().nullable(),
  occasionType,
  productName: z.string(),
  quantity: z.number().int(),
  deliveredAt: z.string().nullable(),
  payable: z.number().int(), // order_supply.total_payable (업체가 받을 공급가)
  // PENDING=아직 지급계획 없음(지급 취소 시에도 여기로 복귀) / PLANNED=지급예정 / PAID=지급완료
  status: vendorSettlementStatus,
  paidAt: z.string().nullable(),
})
export type VendorSettlementItem = z.infer<typeof vendorSettlementItem>

export const vendorSettlementSummary = z.object({
  count: z.number().int(), // 그 달 배송완료 건수
  totalPayable: z.number().int(), // 공급가 합계
  paidAmount: z.number().int(), // 지급 완료액
  pendingAmount: z.number().int(), // 아직 지급 안 된 공급가(PENDING+PLANNED)
})
export type VendorSettlementSummary = z.infer<typeof vendorSettlementSummary>

export const vendorSettlementResponse = z.object({
  month: z.string(), // 조회한 청구월 YYYY-MM (KST)
  summary: vendorSettlementSummary,
  items: z.array(vendorSettlementItem),
})
export type VendorSettlementResponse = z.infer<typeof vendorSettlementResponse>

export const vendorSettlementQuery = z.object({
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional(),
})
export type VendorSettlementQuery = z.infer<typeof vendorSettlementQuery>

export const PayoutErrorCode = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  FORBIDDEN: 'FORBIDDEN',
  PAYOUT_NOT_FOUND: 'PAYOUT_NOT_FOUND',
  NOT_PAYABLE_STATE: 'NOT_PAYABLE_STATE', // 지급은 지급 예정·부분 지급 상태에서만
  NOT_CANCELLABLE: 'NOT_CANCELLABLE', // 취소는 한 푼도 안 나간 지급 예정 상태에서만
  PAID_AMOUNT_INVALID: 'PAID_AMOUNT_INVALID', // 이번 지급액은 0 초과·남은 금액 이하여야 함
} as const
export type PayoutErrorCode = (typeof PayoutErrorCode)[keyof typeof PayoutErrorCode]
